import type { ScoringRule } from "../leagues/types/league.js";

/**
 * ScoringEngine — função pura, sem I/O, sem dependência de Firebase/API.
 * Roda tanto no cliente (se um dia quisermos mostrar uma prévia de pontos)
 * quanto no servidor (api/cron/process-scores.ts), que é onde ela é
 * AUTORITATIVA de verdade (Priority 12: "o cliente não pode enviar uma
 * pontuação que ele mesmo escolheu" — o cliente nunca chama isto para
 * gravar nada, só o cron).
 *
 * REGRA IMPLEMENTADA (documentada explicitamente porque as labels do
 * projeto — "Vencedor + gols do vencedor", "Vencedor + gols do perdedor" —
 * não especificam 100% os casos de empate entre regras; esta é a
 * interpretação usada, PRECISA ser validada com o usuário antes de rodar
 * em produção com dinheiro/prêmio real envolvido):
 *
 * 1. Placar exato (ph==ah && pa==aa)                         → placar_exato
 * 2. Empate acertado, sem placar exato (ph==pa && ah==aa)     → empate
 * 3. Vencedor certo:
 *    a) gols do time vencedor batem exatamente                → vencedor_gols
 *    b) gols do time perdedor batem exatamente (vencedor não)  → vencedor_gols_perdedor
 *    c) nenhum dos dois bate                                   → vencedor
 * 4. Vencedor errado, mas saldo de gols (diferença) igual       → saldo
 * 5. Nenhuma regra bate                                         → 0
 *
 * Só a MELHOR regra aplicável conta (não é somado com as outras).
 */

export type MatchOutcome = "home" | "away" | "draw";

function outcome(home: number, away: number): MatchOutcome {
  if (home > away) return "home";
  if (away > home) return "away";
  return "draw";
}

function ruleValue(rules: ScoringRule[], id: string): number {
  return rules.find((r) => r.id === id)?.points ?? 0;
}

export interface ScoreInput {
  predictedHome: number;
  predictedAway: number;
  actualHome: number;
  actualAway: number;
}

export function calculateMatchPoints(input: ScoreInput, rules: ScoringRule[], exactScoreMultiplier = 1): number {
  const { predictedHome: ph, predictedAway: pa, actualHome: ah, actualAway: aa } = input;

  // 1. Placar exato — único caso que recebe o multiplicador de bônus
  // (Gol de Placa ×2 / Gol de Ouro ×3, itens 15-16). NUNCA aplicado às
  // outras categorias (item 15: "recebe o dobro da pontuação normal do
  // PLACAR EXATO" — não menciona as demais).
  if (ph === ah && pa === aa) {
    return ruleValue(rules, "placar_exato") * exactScoreMultiplier;
  }

  const predictedOutcome = outcome(ph, pa);
  const actualOutcome = outcome(ah, aa);

  if (predictedOutcome === actualOutcome) {
    // 2. Empate certo, mas não exato (ex: previu 1x1, saiu 2x2)
    if (actualOutcome === "draw") {
      return ruleValue(rules, "empate");
    }

    // 3. Vencedor certo
    const winnerIsHome = actualOutcome === "home";
    const predictedWinnerScore = winnerIsHome ? ph : pa;
    const actualWinnerScore = winnerIsHome ? ah : aa;
    const predictedLoser = winnerIsHome ? pa : ph;
    const actualLoser = winnerIsHome ? aa : ah;

    if (predictedWinnerScore === actualWinnerScore) {
      return ruleValue(rules, "vencedor_gols");
    }
    if (predictedLoser === actualLoser) {
      return ruleValue(rules, "vencedor_gols_perdedor");
    }
    return ruleValue(rules, "vencedor");
  }

  // 4. Saldo de gols igual, mesmo com vencedor errado
  const predictedDiff = Math.abs(ph - pa);
  const actualDiff = Math.abs(ah - aa);
  if (predictedDiff === actualDiff) {
    return ruleValue(rules, "saldo");
  }

  return 0;
}
